import { useMemo, useState, type MouseEvent } from 'react';
import { areaPath, linePath, niceTicks, useMeasure, type Series } from './chartUtils';

export interface LineChartProps<T extends Record<string, unknown>> {
  data: T[];
  xKey: keyof T & string;
  series: Series[];
  height?: number;
  /** Formats y-axis ticks and tooltip values. */
  yFormat?: (v: number) => string;
  xFormat?: (v: string) => string;
  /** A dashed reference line — a KPI target, an SLA threshold. */
  target?: { value: number; label: string };
  /** Fill under the line. Only meaningful for a single series; ignored otherwise
   *  because overlapping translucent fills make both unreadable. */
  area?: boolean;
  /** Force the y-axis to start at zero. */
  zeroBased?: boolean;
  /** Shown when there is nothing to plot, so the caller can say what is missing
   *  rather than leaving a bare "no data". */
  emptyMessage?: string;
  /** Makes each point clickable — the nearest one to the click, as the
   *  crosshair snaps — for a drill-down into the period it shows. */
  onSelect?: (row: T, index: number) => void;
}

const PAD = { top: 12, right: 16, bottom: 26, left: 46 };

/**
 * A time-series line chart with a crosshair tooltip.
 *
 * NOT zero-based by default. A service level moving between 78% and 84% is the
 * whole story, and a chart from zero flattens it into a straight line near the
 * top. `zeroBased` is there for the measures where the distance from zero is the
 * point — volumes and counts — and the caller decides which it has.
 */
export function LineChart<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  height = 220,
  yFormat = (v) => String(Math.round(v)),
  xFormat = (v) => v,
  target,
  area = false,
  zeroBased = false,
  emptyMessage = 'No data for this period.',
  onSelect,
}: LineChartProps<T>) {
  const [ref, width] = useMeasure<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const geo = useMemo(() => {
    const w = Math.max(width, 200);
    const innerW = Math.max(w - PAD.left - PAD.right, 10);
    const innerH = Math.max(height - PAD.top - PAD.bottom, 10);

    const values: number[] = [];
    for (const row of data) {
      for (const s of series) {
        const v = Number(row[s.key]);
        if (Number.isFinite(v)) values.push(v);
      }
    }
    if (target) values.push(target.value);

    let min = values.length ? Math.min(...values) : 0;
    let max = values.length ? Math.max(...values) : 1;
    if (zeroBased) min = Math.min(0, min);
    /* Otherwise pad below, so the lowest point is not welded to the axis. The
       `|| 1` covers a flat series, where the span is zero and the padding would
       be too. */
    else min -= (max - min) * 0.12 || 1;
    max += (max - min) * 0.08 || 1;

    const ticks = niceTicks(min, max, 4);
    const yMin = Math.min(...ticks, min);
    const yMax = Math.max(...ticks, max);

    const x = (i: number) =>
      PAD.left + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
    const y = (v: number) => PAD.top + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;

    return { w, innerW, innerH, ticks, x, y, baseline: PAD.top + innerH };
  }, [width, height, data, series, target, zeroBased]);

  if (!data.length) return <div className="empty">{emptyMessage}</div>;

  /* Roughly one x tick per 78px, always keeping the last. Any denser and the
     labels overlap; any sparser and the axis stops locating anything. */
  const tickEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(geo.innerW / 78))));
  const hovered = hover !== null ? data[hover] : null;

  /* Snaps to the nearest point rather than tracking the pointer, so the readout
     always names a real observation. */
  const nearest = (event: MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const rel = event.clientX - rect.left - PAD.left;
    const step = data.length <= 1 ? geo.innerW : geo.innerW / (data.length - 1);
    return Math.min(Math.max(Math.round(rel / step), 0), data.length - 1);
  };
  const onMove = (event: MouseEvent<SVGSVGElement>) => setHover(nearest(event));

  const firstSeries = series[0];

  return (
    <div className="chart-frame" ref={ref}>
      <svg
        className="chart"
        width={geo.w}
        height={height}
        role="img"
        aria-label={`Line chart: ${series.map((s) => s.label).join(', ')}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        onClick={
          onSelect
            ? (event) => {
                const i = nearest(event);
                onSelect(data[i]!, i);
              }
            : undefined
        }
        style={onSelect ? { cursor: 'pointer' } : undefined}
      >
        {geo.ticks.map((t) => (
          <g key={t}>
            <line className="chart-grid" x1={PAD.left} x2={geo.w - PAD.right} y1={geo.y(t)} y2={geo.y(t)} />
            <text className="chart-tick" x={PAD.left - 8} y={geo.y(t)} textAnchor="end" dominantBaseline="middle">
              {yFormat(t)}
            </text>
          </g>
        ))}

        <line className="chart-axis" x1={PAD.left} x2={geo.w - PAD.right} y1={geo.baseline} y2={geo.baseline} />

        {data.map((row, i) =>
          /* A regular tick too close to the always-kept last one gives way to it. */
          (i % tickEvery === 0 && data.length - 1 - i >= tickEvery / 2) || i === data.length - 1 ? (
            <text
              key={`x-${i}`}
              className="chart-tick"
              x={geo.x(i)}
              y={height - 8}
              /* The end labels are anchored inward so they do not clip. */
              textAnchor={i === data.length - 1 ? 'end' : i === 0 ? 'start' : 'middle'}
            >
              {xFormat(String(row[xKey]))}
            </text>
          ) : null,
        )}

        {target && (
          <>
            <line
              className="chart-target"
              x1={PAD.left}
              x2={geo.w - PAD.right}
              y1={geo.y(target.value)}
              y2={geo.y(target.value)}
            />
            <text className="chart-tick" x={geo.w - PAD.right} y={geo.y(target.value) - 5} textAnchor="end">
              {target.label}
            </text>
          </>
        )}

        {area && series.length === 1 && firstSeries && (
          <path
            d={areaPath(
              data
                .map((row, i) => [geo.x(i), geo.y(Number(row[firstSeries.key]))] as [number, number])
                .filter(([, y]) => Number.isFinite(y)),
              geo.baseline,
            )}
            opacity={0.12}
            style={{ fill: firstSeries.color }}
          />
        )}

        {series.map((s) => (
          <path
            key={s.key}
            d={linePath(
              data
                .map((row, i) => [geo.x(i), geo.y(Number(row[s.key]))] as [number, number])
                .filter(([, y]) => Number.isFinite(y)),
            )}
            fill="none"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ stroke: s.color }}
          />
        ))}

        {hover !== null && hovered && (
          <>
            <line className="chart-crosshair" x1={geo.x(hover)} x2={geo.x(hover)} y1={PAD.top} y2={geo.baseline} />
            {series.map((s) => {
              const v = Number(hovered[s.key]);
              if (!Number.isFinite(v)) return null;
              return (
                <circle
                  key={s.key}
                  cx={geo.x(hover)}
                  cy={geo.y(v)}
                  r={4.5}
                  strokeWidth={2}
                  style={{ fill: s.color, stroke: 'var(--surface-card)' }}
                />
              );
            })}
          </>
        )}
      </svg>

      {hovered && hover !== null && (
        <div
          className="chart-tooltip"
          /* Clamped so the readout stays inside the frame at both ends. */
          style={{ left: Math.min(Math.max(geo.x(hover), 70), geo.w - 70), top: PAD.top + 2 }}
        >
          <div className="chart-tt-title">{xFormat(String(hovered[xKey]))}</div>
          {series.map((s) => (
            <div className="chart-tt-row" key={s.key}>
              <span className="chart-swatch" style={{ background: s.color }} />
              <span>{s.label}</span>
              <span className="chart-tt-value">
                {Number.isFinite(Number(hovered[s.key])) ? yFormat(Number(hovered[s.key])) : '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      {series.length > 1 && (
        <div className="chart-legend">
          {series.map((s) => (
            <span className="chart-legend-item" key={s.key}>
              <span className="chart-legend-swatch line" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
